import api from '@pray/shared/utils/api';

/**
 * Valid content types for priority reordering.
 */
export type TaggableContentType =
  | 'content'
  | 'content_series'
  | 'artist'
  | 'book_chapter'
  | 'daily_item'
  | 'daily_series';

/**
 * A single priority update for a tagging.
 */
export interface PriorityUpdate {
  tagging_id: string;
  priority: number;
}

/**
 * Parameters for bulk updating tagging priorities.
 */
export interface UpdateTaggingPrioritiesParams {
  /** The tag ID that these taggings belong to */
  tagId: string;
  /** The content type being reordered (priorities are type-specific) */
  contentType: TaggableContentType;
  /** Array of priority updates with tagging IDs and new priorities */
  updates: PriorityUpdate[];
}

/**
 * Response from the bulk priority update endpoint.
 */
export interface UpdateTaggingPrioritiesResponse {
  data: {
    success: boolean;
    updated_count: number;
    errors: string[];
  };
}

/**
 * Bulk update priorities for multiple taggings.
 *
 * This allows reordering items within a tag by updating their priorities.
 * Priorities are type-specific - episodes, series, and artists each have
 * their own priority order within a tag.
 *
 * The operation is atomic - all updates succeed or all fail.
 *
 * @param params - The update parameters
 * @returns Promise resolving to the update result
 *
 * @example
 * ```ts
 * await updateTaggingPriorities({
 *   tagId: 'abc-123',
 *   contentType: 'content',
 *   updates: [
 *     { tagging_id: 'tag-1', priority: 1 },
 *     { tagging_id: 'tag-2', priority: 2 },
 *     { tagging_id: 'tag-3', priority: 3 },
 *   ],
 * });
 * ```
 */
export async function updateTaggingPriorities({
  tagId,
  contentType,
  updates,
}: UpdateTaggingPrioritiesParams): Promise<UpdateTaggingPrioritiesResponse> {
  if (!tagId) {
    throw new Error('Tag ID is required');
  }

  if (!contentType) {
    throw new Error('Content type is required');
  }

  if (!updates || updates.length === 0) {
    throw new Error('At least one priority update is required');
  }

  const url = '/web/studio/taggings/priorities';

  const body = {
    tag_id: tagId,
    content_type: contentType,
    updates,
  };

  const response = await api.executePutRequest<UpdateTaggingPrioritiesResponse>(url, body);
  return response.data as UpdateTaggingPrioritiesResponse;
}
